import { api } from '@/lib/api';

export type V2Pagination = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

/** Corps `data` d’une réponse API Lyrra. */
export function unwrapV2Data<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export function unwrapV2Paginated<T>(raw: unknown): { items: T[]; pagination: V2Pagination | null } {
  if (!raw || typeof raw !== 'object') return { items: [], pagination: null };
  const o = raw as { data?: T[]; pagination?: V2Pagination };
  return {
    items: Array.isArray(o.data) ? o.data : [],
    pagination: o.pagination ?? null,
  };
}

/** Statuts Prisma `TrainingCampaign` → libellés UI (types v2 front). */
export function mapCampaignStatusForUi(raw: string | undefined | null): string {
  const s = String(raw || '');
  const m: Record<string, string> = {
    campaign_draft: 'draft',
    scheduled: 'scheduled',
    campaign_active: 'active',
    paused: 'paused',
    campaign_completed: 'completed',
    cancelled: 'cancelled',
  };
  return m[s] || s || 'draft';
}

export const v2ReportingApi = {
  dashboard: () => api.get<unknown>('/v2/reporting/dashboard').then(unwrapV2Data),
  kpis: () => api.get<unknown>('/v2/reporting/dashboard/kpis').then(unwrapV2Data),
  summary: () => api.get<unknown>('/v2/reporting/dashboard/summary').then(unwrapV2Data),
  trendsCompletion: (months?: number) =>
    api.get<unknown>('/v2/reporting/trends/completion', { months: months ?? 12 }).then(unwrapV2Data),
  trendsBudget: (years?: number) =>
    api.get<unknown>('/v2/reporting/trends/budget', { years: years ?? 3 }).then(unwrapV2Data),
  budgetByDepartment: (year?: number) =>
    api
      .get<unknown>('/v2/reporting/budget/by-department', { year: year ?? new Date().getFullYear() })
      .then(unwrapV2Data),
  budgetForecast: () => api.get<unknown>('/v2/reporting/budget/forecast').then(unwrapV2Data),
  /** Coût par complétion (budget consommé / parcours terminés) — remplace l’ancien `/reporting/roi`. */
  costPerCompletion: (year?: number) =>
    api
      .get<unknown>('/v2/reporting/cost-per-completion', year ? { year } : {})
      .then(unwrapV2Data) as Promise<V2CostPerCompletion>,
};

export type V2CostPerCompletion = {
  year: number;
  totalCost: number;
  completions: number;
  costPerCompletion: number;
};

export type V2ReportType = 'codir' | 'report_department' | 'custom';
export type V2ReportFormat = 'xlsx' | 'csv';
export type V2ReportSection =
  | 'summary'
  | 'completion_trend'
  | 'budget_by_department'
  | 'cost_per_completion'
  | 'programs'
  | 'competency_trend';
export type V2ReportStatus = 'generating' | 'report_completed' | 'report_failed';

export type V2GeneratedReport = {
  id: string;
  title: string;
  type: string;
  format: string;
  status: V2ReportStatus;
  fileSize: number | null;
  period: { year?: number } | null;
  metrics: { sections?: V2ReportSection[]; error?: string; rowCounts?: Record<string, number> } | null;
  sentAt: string | null;
  sentTo: string[] | null;
  configurationId: string | null;
  createdAt: string;
  generatedBy: { id: string; firstName: string | null; lastName: string | null } | null;
};

export type V2ReportSchedule = {
  id: string;
  title: string;
  type: V2ReportType;
  schedule: string | null;
  format: string[];
  recipients: string[] | null;
  metrics: { sections?: V2ReportSection[] } | null;
  isActive: boolean;
  lastGeneratedAt: string | null;
  createdAt: string;
};

export type V2ReportScheduleInput = {
  title: string;
  type: V2ReportType;
  schedule: string;
  format: V2ReportFormat[];
  recipients: string[];
  sections?: V2ReportSection[];
  isActive?: boolean;
};

export const v2ReportsApi = {
  list: (params?: { page?: number; limit?: number; status?: V2ReportStatus }) =>
    api.get<unknown>('/v2/reporting/reports', params ?? {}).then((raw) => unwrapV2Paginated<V2GeneratedReport>(raw)),
  generate: (body: {
    title: string;
    type: V2ReportType;
    format: V2ReportFormat;
    sections?: V2ReportSection[];
    year?: number;
  }) => api.post<unknown>('/v2/reporting/reports/generate', body).then(unwrapV2Data) as Promise<V2GeneratedReport>,
  remove: (id: string) => api.delete<unknown>(`/v2/reporting/reports/${id}`),
  /** Téléchargement authentifié du fichier (XLSX / CSV) puis enregistrement local. */
  download: async (report: Pick<V2GeneratedReport, 'id' | 'title' | 'format' | 'createdAt'>) => {
    const blob = await api.get<Blob>(`/v2/reporting/reports/${report.id}/download`, undefined, { responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.title.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'rapport'}-${report.createdAt.slice(0, 10)}.${report.format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  },
  schedules: () => api.get<unknown>('/v2/reporting/reports/schedules').then(unwrapV2Data) as Promise<V2ReportSchedule[]>,
  createSchedule: (body: V2ReportScheduleInput) =>
    api.post<unknown>('/v2/reporting/reports/schedule', body).then(unwrapV2Data) as Promise<V2ReportSchedule>,
  updateSchedule: (id: string, body: Partial<V2ReportScheduleInput>) =>
    api.put<unknown>(`/v2/reporting/reports/schedules/${id}`, body).then(unwrapV2Data) as Promise<V2ReportSchedule>,
  removeSchedule: (id: string) => api.delete<unknown>(`/v2/reporting/reports/schedules/${id}`),
  runSchedule: (id: string) => api.post<unknown>(`/v2/reporting/reports/schedules/${id}/run`, {}),
};

export const v2CampaignsApi = {
  stats: () => api.get<unknown>('/v2/campaigns/stats').then(unwrapV2Data),
  list: (params: { page?: number; limit?: number; search?: string; status?: string }) =>
    api.get<unknown>('/v2/campaigns', params).then(unwrapV2Paginated),
  get: (id: string) => api.get<unknown>(`/v2/campaigns/${id}`).then(unwrapV2Data),
  create: (body: Record<string, unknown>) => api.post<unknown>('/v2/campaigns', body).then(unwrapV2Data),
  update: (id: string, body: Record<string, unknown>) => api.put<unknown>(`/v2/campaigns/${id}`, body).then(unwrapV2Data),
  remove: (id: string) => api.delete<unknown>(`/v2/campaigns/${id}`),
  launch: (id: string) => api.post<unknown>(`/v2/campaigns/${id}/launch`, {}).then(unwrapV2Data),
  complete: (id: string) => api.post<unknown>(`/v2/campaigns/${id}/complete`, {}).then(unwrapV2Data),
  cancel: (id: string) => api.post<unknown>(`/v2/campaigns/${id}/cancel`, {}).then(unwrapV2Data),
  analytics: (id: string) => api.get<unknown>(`/v2/campaigns/${id}/analytics`).then(unwrapV2Data),
  participants: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<unknown>(`/v2/campaigns/${id}/participants`, params ?? {}).then(unwrapV2Paginated),
  assign: (
    id: string,
    body: { userIds?: string[]; learningGroupIds?: string[]; serviceIds?: string[] },
  ) => api.post<unknown>(`/v2/campaigns/${id}/assign`, body).then(unwrapV2Data),
  assignableUsers: (params?: { search?: string; limit?: number }) =>
    api.get<unknown>('/v2/campaigns/assignable-users', params ?? {}).then(unwrapV2Data),
};

export const v2CatalogApi = {
  categories: () => api.get<unknown>('/v2/catalog/categories').then(unwrapV2Data),
  listItems: (params: {
    page?: number;
    limit?: number;
    search?: string;
    categoryId?: string;
    published?: string;
    /** Inclure les parcours EduFlow publiés des formateurs de l’établissement (défaut côté API si omis). */
    includeFlows?: string;
  }) => api.get<unknown>('/v2/catalog/items', params).then(unwrapV2Paginated),
  getItem: (id: string) => api.get<unknown>(`/v2/catalog/items/${id}`).then(unwrapV2Data),
  popular: (limit?: number) =>
    api.get<unknown>('/v2/catalog/popular', { limit: limit ?? 12 }).then(unwrapV2Data),
  enroll: (id: string) => api.post<unknown>(`/v2/catalog/items/${id}/enroll`, {}).then(unwrapV2Data),
};

export const v2CompetenciesApi = {
  domains: () => api.get<unknown>('/v2/competencies/domains').then(unwrapV2Data),
  /** Création référentiel (droits équivalent `/v2/competencies` RH). */
  createDomain: (body: { name: string; description?: string }) =>
    api.post<unknown>('/v2/competencies/domains', body).then(unwrapV2Data),
  createFamily: (body: { domainId: string; name: string; description?: string }) =>
    api.post<unknown>('/v2/competencies/families', body).then(unwrapV2Data),
  createCompetency: (body: {
    familyId: string;
    name: string;
    description?: string;
    maxLevel?: number;
  }) => api.post<unknown>('/v2/competencies', body).then(unwrapV2Data),
  deleteDomain: (id: string) => api.delete<unknown>(`/v2/competencies/domains/${id}`).then(unwrapV2Data),
  deleteFamily: (id: string) => api.delete<unknown>(`/v2/competencies/families/${id}`).then(unwrapV2Data),
  deleteCompetency: (id: string) => api.delete<unknown>(`/v2/competencies/${id}`).then(unwrapV2Data),
  /** Arbre domaines → familles → compétences (niveaux attendus, descripteurs de niveaux). */
  framework: () => api.get<unknown>('/v2/competencies/framework').then(unwrapV2Data),
  /** Moyennes par équipe/service (libellé `User.department`) × compétence (évals validées). */
  heatmap: () => api.get<unknown>('/v2/competencies/heatmap').then(unwrapV2Data),
  gapCritical: () => api.get<unknown>('/v2/competencies/gap-analysis/critical').then(unwrapV2Data),
  gapAnalysis: (params?: { userId?: string; departmentLabel?: string }) =>
    api.get<unknown>('/v2/competencies/gap-analysis', params ?? {}).then(unwrapV2Data),
};

export const v2ManagerApi = {
  team: () => api.get<unknown>('/v2/manager/team').then(unwrapV2Data),
};

export const v2WorkflowsApi = {
  listRequests: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<unknown>('/v2/workflows/requests', params ?? {}).then(unwrapV2Paginated),
  /** Demandes en attente de ma décision (approbateur unique `assignedTo`, ou délégation active). */
  pendingApprovals: (params?: { page?: number; limit?: number }) =>
    api.get<unknown>('/v2/workflows/approvals/pending', params ?? {}).then(unwrapV2Paginated),
};

export type V2TrainingRequestPerson = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
};

export type V2TrainingContentRequestRow = {
  id: string;
  institutionId: string;
  templateId: string;
  requesterId: string;
  assignedToId?: string | null;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  data: Record<string, unknown>;
  requester?: V2TrainingRequestPerson | null;
  assignedTo?: V2TrainingRequestPerson | null;
};

export const v2TrainingContentRequestsApi = {
  list: (params?: { status?: string }) =>
    api.get<unknown>('/v2/training-content-requests', params ?? {}).then(unwrapV2Data) as Promise<
      V2TrainingContentRequestRow[]
    >,
  create: (body: Record<string, unknown>) =>
    api.post<unknown>('/v2/training-content-requests', body).then(unwrapV2Data),
  approve: (id: string) =>
    api.post<unknown>(`/v2/training-content-requests/${id}/approve`, {}).then(unwrapV2Data),
  reject: (id: string, body?: { reason?: string }) =>
    api.post<unknown>(`/v2/training-content-requests/${id}/reject`, body ?? {}).then(unwrapV2Data),
  assignees: () =>
    api.get<unknown>('/v2/training-content-requests/assignees').then(unwrapV2Data) as Promise<
      V2TrainingRequestPerson[]
    >,
  update: (
    id: string,
    body: {
      title?: string;
      description?: string;
      justification?: string;
      preferredDate?: string;
      providerHint?: string;
      rejectReason?: string;
      status?: string;
      assignedToId?: string | null;
    },
  ) => api.patch<unknown>(`/v2/training-content-requests/${id}`, body).then(unwrapV2Data),
  remove: (id: string) =>
    api.delete<unknown>(`/v2/training-content-requests/${id}`).then(unwrapV2Data),
};
